import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import AppliedFilters from '../reporting_utils/AppliedFilters';
import ReportFilters from '../reporting_utils/ReportFilters';
import CallDetailsCount from './CallDetailsCount';
import CallDetailsTable from './CallDetailsTable';
import DateRange from '../reporting_utils/DateRange';
import RefreshButton from '../reporting_utils/RefreshButton';
import useFiltersState from '../reporting_utils/useFiltersState';
import ReportScheduler from '../reporting_utils/ReportScheduler';
import moment from 'moment';
import { Common } from '../../utils/common';
import ErrorBoundary from '../reporting_utils/ErrorBoundary';
import CurrentDomains from '../reporting_utils/CurrentDomains';
import { getFilterValues } from '../reporting_utils/misc';
import useDomainSettings from '../reporting_utils/useDomainSettings';
import EmptyReport from '../reporting_utils/EmptyReport';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

const pageLoadingFrame = window.pageLoadingFrame;

// Used as initial values for the filter state and as a reference
// for what to set the values to when the filters are cleared
const defaultFilterState = {
  medium_type: [],
  ds_appt_set: false,
  ds_alert_sent: false,
  dial_status: [],
  ds_department: [],
  ds_call_status: [],
  ls_call_type: [],
  tag_ids: [],
};

const queryClient = new QueryClient();

function CallDetails({ defaultFiltersFromURL, controllerName, domainId, selectInputs }) {
  const [tableData, setTableData] = useState();
  const [callData, setCallData] = useState(null);
  const [filters, setFilters] = useFiltersState(
    defaultFilterState,
    defaultFiltersFromURL,
    domainId
  );
  const [sidebarIsOpen, setSidebarIsOpen] = useState(false);
  const [controlledDateRange, setControlledDateRange] = useState({
    start: moment(filters.start_date, 'YYYYMMDD'),
    end: moment(filters.end_date, 'YYYYMMDD'),
  });

  const { isAutomotiveIndustry, isLoading } = useDomainSettings(
    filters.domains,
    filters.show_inactive
  );

  // Wrapped in useCallback so these functions only get recreated when the filters
  // state changes. Otherwise they will be recreated every render, which will trigger
  // the useEffect to run every render, causing an infinite loop of rerenders.
  const fetchTableData = useCallback(async () => {
    if (!filters.domains) {
      return;
    }
    pageLoadingFrame('show');
    try {
      const res = await fetch('/filtered_call_details', {
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name=csrf-token]').content,
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify(getFilterValues(filters)),
      });
      const { data } = await res.json();
      setTableData(data);
    } catch (error) {
      console.error(error);
      Common.notify('Failed to fetch report data', 'error');
    }
    pageLoadingFrame('hide');
  }, [filters]);

  const fetchCountData = useCallback(async () => {
    if (!filters.domains) {
      return;
    }
    try {
      const res = await fetch('/filtered_call_details_counts_el', {
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name=csrf-token]').content,
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify(getFilterValues(filters)),
      });
      const { data } = await res.json();
      setCallData(data);
    } catch (error) {
      console.error(error);
      Common.notify('Failed to fetch report data', 'error');
    }
  }, [filters]);

  useEffect(() => {
    // Fetches table and call count data whenever the filters change
    // This effect only runs when these functions change, which only happens
    // when the filters change as specified in useCallback's dependency array.
    fetchCountData();
    fetchTableData();
  }, [fetchCountData, fetchTableData]);

  return (
    <QueryClientProvider client={queryClient}>
      <div className='report-details-container'>
        <div className='report-details-header'>
          <div className='report-details-title'>
            <h1>Call Details</h1>
            <p>A detailed report of all calls for your account</p>
          </div>
          <div className='panel-header'>
            <ErrorBoundary>
              {!Common.in_crm_mode() && (
                <ReportScheduler controllerName={controllerName} filters={filters} />
              )}
              <RefreshButton dataCallbacks={[fetchCountData, fetchTableData]} />
              {!sidebarIsOpen && (
                <DateRange
                  controlledDateRange={controlledDateRange}
                  setControlledDateRange={setControlledDateRange}
                  filters={filters}
                  setFilters={setFilters}
                />
              )}
            </ErrorBoundary>
          </div>
        </div>
        <ReportFilters
          filters={filters}
          defaultFilterState={defaultFilterState}
          open={sidebarIsOpen}
          setOpen={setSidebarIsOpen}
          setFilters={setFilters}
          controlledDateRange={controlledDateRange}
          setControlledDateRange={setControlledDateRange}
          selectInputs={selectInputs}
        />
        {filters.domains ? (
          <ErrorBoundary>
            <CallDetailsCount callData={callData} />
            <div className='applied-filters-container'>
              <div style={{ marginBottom: '20px' }}>
                <button
                  className='btn btn-primary'
                  onClick={() => setSidebarIsOpen(!sidebarIsOpen)}
                >
                  <span className='fa fa-plus' /> Add Filters
                </button>
              </div>
              {!Common.in_crm_mode() && (
                <CurrentDomains filters={filters} setFilters={setFilters} />
              )}
              <ErrorBoundary>
                <AppliedFilters
                  defaultFilterState={defaultFilterState}
                  filters={filters}
                  setFilters={setFilters}
                />
              </ErrorBoundary>
            </div>
            {isLoading || tableData === undefined ? null : (
              <CallDetailsTable
                key={Math.random()}
                isAutomotiveIndustry={isAutomotiveIndustry}
                tableData={tableData}
              />
            )}
          </ErrorBoundary>
        ) : (
          <EmptyReport onClick={() => setSidebarIsOpen(!sidebarIsOpen)} />
        )}
      </div>
    </QueryClientProvider>
  );
}

CallDetails.propTypes = {
  defaultFiltersFromURL: PropTypes.object.isRequired,
  controllerName: PropTypes.string.isRequired,
  domainId: PropTypes.string,
  selectInputs: PropTypes.object.isRequired,
};

export default CallDetails;
